import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, TrendingUp, TrendingDown, Minus,
  Youtube, BookOpen, Radio, ExternalLink, Flag, Share2,
} from 'lucide-react';

function XIcon({ size = 14, className = '' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.91-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}
import { API_BASE_URL } from '../config';
import ShareModal from '../components/ui/ShareModal';
import PriceTimelineChart from '../components/analysts/PriceTimelineChart';
import QuotaUpgradeModal from '../components/ui/QuotaUpgradeModal';

function getAuthHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function ReportButton({ predictionId }) {
  const [status, setStatus] = useState('idle'); // idle | loading | done | error

  async function handleReport() {
    if (status !== 'idle') return;
    setStatus('loading');
    try {
      await axios.post(
        `${API_BASE_URL}/api/research/predictions/${predictionId}/report/`,
        { reason: 'other' },
        { headers: getAuthHeader() },
      );
      setStatus('done');
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 2000);
    }
  }

  return (
    <button
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleReport(); }}
      title={status === 'done' ? 'Reported' : 'Report this prediction'}
      className={`p-1.5 rounded-lg transition-colors ${
        status === 'done'
          ? 'text-orange-500 bg-orange-50 cursor-default'
          : status === 'error'
          ? 'text-red-500 bg-red-50'
          : 'text-gray-400 hover:text-orange-500 hover:bg-orange-50'
      }`}
    >
      <Flag size={14} />
    </button>
  );
}

const PLATFORM_ICONS = {
  twitter: XIcon,
  youtube: Youtube,
  substack: BookOpen,
  podcast: Radio,
};

const DIRECTION_CONFIG = {
  bullish: {
    label: 'Bullish', icon: TrendingUp,
    bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200',
  },
  bearish: {
    label: 'Bearish', icon: TrendingDown,
    bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200',
  },
  neutral: {
    label: 'Neutral', icon: Minus,
    bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200',
  },
};

function formatReturn(pct) {
  if (pct === null || pct === undefined) return 'N/A';
  const n = parseFloat(pct);
  if (n === 0) return 'N/A';
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
}

function formatFollowers(n) {
  if (!n) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function formatDate(iso) {
  if (!iso) return '';
  // Parse the UTC date/time directly from the ISO string to avoid timezone shifts
  const [datePart, timePart] = iso.slice(0, 16).split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const h = hour % 12 || 12;
  const ampm = hour < 12 ? 'AM' : 'PM';
  return `${monthNames[month - 1]} ${day}, ${year} · ${h}:${String(minute).padStart(2, '0')} ${ampm} UTC`;
}

export default function SymbolAnalystPage() {
  const { exchange, ticker } = useParams();

  const isGuest = !localStorage.getItem('token');
  const [quotaError, setQuotaError] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [limitError, setLimitError] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [loadingPredictions, setLoadingPredictions] = useState(true);

  // Filters
  const [directionFilter, setDirectionFilter] = useState('');
  const [analystFilter, setAnalystFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [ordering, setOrdering] = useState('-published_at');

  // Fetch symbol detail
  useEffect(() => {
    const fetch = async () => {
      setLoadingDetail(true);
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/research/symbols/${exchange}/${ticker}/`,
          { headers: getAuthHeader() },
        );
        setDetail(res.data);
      } catch (err) {
        if (err.response?.status === 402) {
          setQuotaError(err.response.data);
        } else if (err.response?.status === 429) {
          setLimitError(err.response.data);
        }
        setDetail(null);
      } finally {
        setLoadingDetail(false);
      }
    };
    fetch();
  }, [exchange, ticker]);

  // Fetch predictions (re-fetches when filters change)
  useEffect(() => {
    const fetch = async () => {
      setLoadingPredictions(true);
      try {
        const params = new URLSearchParams({ ordering });
        if (directionFilter) params.set('direction', directionFilter);
        if (analystFilter) params.set('analyst', analystFilter);
        if (platformFilter) params.set('platform', platformFilter);
        const res = await axios.get(
          `${API_BASE_URL}/api/research/symbols/${exchange}/${ticker}/predictions/?${params}`,
          { headers: getAuthHeader() },
        );
        setPredictions(res.data);
      } catch {
        setPredictions([]);
      } finally {
        setLoadingPredictions(false);
      }
    };
    fetch();
  }, [exchange, ticker, directionFilter, analystFilter, platformFilter, ordering]);

  // Unique analysts from the unfiltered predictions for the filter dropdown
  const analystOptions = React.useMemo(() => {
    const seen = new Map();
    predictions.forEach((p) => {
      if (!seen.has(p.analyst_slug)) seen.set(p.analyst_slug, p.analyst_name);
    });
    return [...seen.entries()];
  }, [predictions]);

  if (loadingDetail) {
    return (
      <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-64 bg-white rounded-xl border border-gray-100" />
        </div>
      </div>
    );
  }

  if (limitError) {
    return (
      <div className="flex-1 overflow-y-auto bg-gray-50 p-6 flex flex-col">
        <Link
          to="/symbols"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-5 transition-colors max-w-3xl mx-auto w-full"
        >
          <ArrowLeft size={15} /> Symbols
        </Link>
        <div className="text-center text-gray-500 py-10">
          Rate limit reached. Please try again shortly.
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
        <Link to="/symbols" className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 mb-6">
          <ArrowLeft size={16} /> Back to Symbols
        </Link>
        <p className="text-gray-500">Symbol not found: {exchange}/{ticker}</p>
      </div>
    );
  }

  const returnColor = (pct) => {
    const n = parseFloat(pct ?? 0);
    return n > 0 ? 'text-emerald-600' : n < 0 ? 'text-red-600' : 'text-gray-500';
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
      {/* Back */}
      <Link
        to="/symbols"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-5 transition-colors"
      >
        <ArrowLeft size={15} /> Symbols
      </Link>

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 mb-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold text-gray-900">{detail.ticker}</h1>
              <span className="text-sm px-2.5 py-0.5 bg-gray-100 text-gray-600 rounded-md font-medium">
                {detail.exchange}
              </span>
              <button
                onClick={() => setIsShareModalOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-sm font-semibold ml-2"
                title="Share Symbol"
              >
                <Share2 size={14} /> Share
              </button>
            </div>
            <p className="text-gray-500 mt-1">{detail.name}</p>
            {detail.latest_close_price != null && (
              <div className="mt-2 flex items-baseline gap-3 flex-wrap">
                <span className="text-3xl font-semibold text-gray-900">
                  ${Number(detail.latest_close_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                {detail.price_change != null && detail.price_change_pct != null && (() => {
                  const up = detail.price_change >= 0;
                  const color = up ? 'text-emerald-600' : 'text-red-500';
                  const sign = up ? '+' : '';
                  return (
                    <span className={`text-sm font-medium ${color}`}>
                      {sign}{Number(detail.price_change).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {' '}({sign}{Number(detail.price_change_pct).toFixed(2)}%)
                    </span>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Summary stats */}
          <div className="flex gap-6 text-center">
            <div>
              <p className="text-2xl font-bold text-gray-900">{detail.analyst_count}</p>
              <p className="text-xs text-gray-500 mt-0.5">Analysts</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{detail.prediction_count}</p>
              <p className="text-xs text-gray-500 mt-0.5">Predictions</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">{detail.bullish_count}</p>
              <p className="text-xs text-gray-500 mt-0.5">Bullish</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-500">{detail.bearish_count}</p>
              <p className="text-xs text-gray-500 mt-0.5">Bearish</p>
            </div>
          </div>
        </div>
      </div>

      {/* Price chart */}
      <div className="mb-5">
        <PriceTimelineChart
          ticker={ticker}
          exchange={exchange}
          predictions={predictions}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        {/* Direction chips */}
        <div className="flex items-center gap-1.5">
          {['', 'bullish', 'bearish', 'neutral'].map((d) => (
            <button
              key={d}
              onClick={() => setDirectionFilter(d)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                directionFilter === d
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
            >
              {d ? d.charAt(0).toUpperCase() + d.slice(1) : 'All'}
            </button>
          ))}
        </div>

        {/* Analyst filter */}
        {analystOptions.length > 1 && (
          <select
            value={analystFilter}
            onChange={(e) => setAnalystFilter(e.target.value)}
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none"
          >
            <option value="">All Analysts</option>
            {analystOptions.map(([slug, name]) => (
              <option key={slug} value={slug}>{name}</option>
            ))}
          </select>
        )}

        {/* Platform filter */}
        <select
          value={platformFilter}
          onChange={(e) => setPlatformFilter(e.target.value)}
          className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none"
        >
          <option value="">All Platforms</option>
          <option value="twitter">Twitter</option>
          <option value="youtube">YouTube</option>
          <option value="substack">Substack</option>
          <option value="podcast">Podcast</option>
        </select>

        {/* Sort */}
        <select
          value={ordering}
          onChange={(e) => setOrdering(e.target.value)}
          className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none ml-auto"
        >
          <option value="-published_at">Most Recent</option>
          <option value="published_at">Oldest First</option>
          <option value="-current_return_pct">Best Return</option>
          <option value="current_return_pct">Worst Return</option>
        </select>
      </div>

      {/* Predictions list */}
      <div className="relative">
      {loadingPredictions ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse h-24" />
          ))}
        </div>
      ) : predictions.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Minus size={32} className="mx-auto mb-2 opacity-30" />
          <p>No predictions match the current filters.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {predictions.map((pred) => {
            const dir = DIRECTION_CONFIG[pred.direction] || DIRECTION_CONFIG.neutral;
            const DirIcon = dir.icon;
            const PlatformIcon = PLATFORM_ICONS[pred.platform] || null;
            const retPct = parseFloat(pred.current_return_pct ?? 0);

            return (
              <div
                key={pred.id}
                className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start gap-4">
                  {/* Analyst avatar */}
                  <div className="w-10 h-10 rounded-full bg-gray-100 shrink-0 overflow-hidden">
                    {pred.analyst_image_url ? (
                      <img
                        src={pred.analyst_image_url} 
                        alt={pred.analyst_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-sm font-semibold text-gray-600">
                        {pred.analyst_name?.[0] ?? '?'}
                      </div>
                    )}
                  </div>

                  {/* Main content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-2 mb-1">
                      <Link
                        to={`/analysts/${pred.analyst_slug}`}
                        className="text-sm font-semibold text-gray-800 hover:text-gray-900"
                      >
                        {pred.analyst_name}
                      </Link>
                      {PlatformIcon && <PlatformIcon size={13} className="text-gray-400" />}
                      {/* Analyst stats — avg return + followers */}
                      {pred.analyst_first_call_return != null && (() => {
                        const r = parseFloat(pred.analyst_first_call_return);
                        return (
                          <span className={`text-[11px] font-medium ${r >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {r >= 0 ? '+' : ''}{r.toFixed(1)}% avg
                          </span>
                        );
                      })()}
                      {formatFollowers(pred.analyst_twitter_followers) && (
                        <span className="text-[11px] text-gray-400">
                          {formatFollowers(pred.analyst_twitter_followers)} followers
                        </span>
                      )}
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${dir.bg} ${dir.text} ${dir.border}`}>
                        <DirIcon size={11} /> {dir.label}
                      </span>
                      <span className="text-xs text-gray-400">{formatDate(pred.published_at)}</span>
                    </div>

                    {/* Content preview */}
                    {pred.content && (
                      <p className="text-xs text-gray-600 line-clamp-2 mb-2">{pred.content}</p>
                    )}

                    {/* Metrics */}
                    <div className="flex flex-wrap items-center gap-4 text-xs">
                      {pred.price_at_prediction && (
                        <span className="text-gray-500">
                          Entry: <span className="text-gray-700 font-medium">
                            ${parseFloat(pred.price_at_prediction).toFixed(2)}
                          </span>
                        </span>
                      )}
                      {pred.current_price && (
                        <span className="text-gray-500">
                          Now: <span className="text-gray-700 font-medium">
                            ${parseFloat(pred.current_price).toFixed(2)}
                          </span>
                        </span>
                      )}
                      <span className={`font-semibold text-sm ${returnColor(pred.current_return_pct)}`}>
                        {formatReturn(pred.current_return_pct)}
                      </span>
                      {pred.stated_price_target && (
                        <span className="text-gray-500">
                          Target: <span className="font-medium text-gray-700">
                            ${parseFloat(pred.stated_price_target).toFixed(2)}
                          </span>
                          {pred.stated_timeframe && ` ${pred.stated_timeframe}`}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action links */}
                  <div className="flex items-center gap-2 shrink-0">
                    <ReportButton predictionId={pred.id} />
                    {pred.source_url && (
                      <a
                        href={pred.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                        title="View source"
                      >
                        <ExternalLink size={14} />
                      </a>
                    )}
                    <Link
                      to={`/analysts/predictions/${pred.id}`}
                      className="text-xs px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                    >
                      Details
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Blur overlay for guests */}
      {isGuest && !loadingPredictions && predictions.length > 0 && (
        <div className="absolute inset-0 backdrop-blur-sm bg-white/60 rounded-xl flex flex-col items-center justify-center gap-3">
          <p className="text-sm font-medium text-gray-700">Sign in to see analyst names and return data</p>
          <Link
            to="/login"
            className="px-5 py-2 bg-gradient-to-br from-[#4F8EF7] to-[#2c72d9] text-white rounded-lg font-semibold text-sm shadow hover:brightness-110 active:scale-95 transition-all"
          >
            Sign In
          </Link>
        </div>
      )}
      </div>

      <QuotaUpgradeModal
        quotaError={quotaError}
        onClose={() => setQuotaError(null)}
      />

      {detail && (
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          title="Share Symbol"
          shareUrl={window.location.href}
          imageUrl={detail.og_image_url || `${API_BASE_URL}/api/research/og/symbol/${exchange}/${ticker}/`}
          downloadUrl={`${API_BASE_URL}/api/research/og/symbol/${exchange}/${ticker}/?download=true`}
          shareText={`Check out the best analyst predictions for ${detail.ticker} on TraderBro!`}
        />
      )}
    </div>
  );
}
